import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useLanguage } from '@/contexts/LanguageContext';
import { loc } from '@/lib/localize';

export interface TourPageReview {
  id: string;
  author: string;
  avatar: string;
  rating: number;
  review_date: string;
  title: string;
  content: string;
  photo: string;
  sort_order: number;
}

interface TourPageReviewRow extends TourPageReview {
  title_vi: string | null;
  content_vi: string | null;
}

export function useTourPageReviews() {
  const [rows, setRows] = useState<TourPageReviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { language } = useLanguage();

  useEffect(() => {
    let cancelled = false;

    async function fetchReviews() {
      try {
        setLoading(true);
        setError(null);

        const { data, error: err } = await supabase
          .from('vietnamtravel_tour_page_reviews')
          .select('id, author, avatar, rating, review_date, title, title_vi, content, content_vi, photo, sort_order')
          .order('sort_order', { ascending: true });

        if (err) throw err;
        if (!cancelled) setRows((data ?? []) as TourPageReviewRow[]);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load reviews');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchReviews();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const reviews: TourPageReview[] = rows.map((r) => ({
    id: r.id,
    author: r.author,
    avatar: r.avatar ?? '',
    rating: r.rating ?? 0,
    review_date: r.review_date ?? '',
    title: loc(language, r.title ?? '', r.title_vi),
    content: loc(language, r.content ?? '', r.content_vi),
    photo: r.photo ?? '',
    sort_order: r.sort_order ?? 0,
  }));

  return { reviews, loading, error, refresh };
}